/**
 * What you get when you walk up to a painting in the Endless Palace and click
 * it: the frame comes off the wall and opens.
 *
 * The card inside is the app's own card — the same component the Saves tab
 * renders, with the same staging-tray, favourite and tag controls — so a save
 * found in the building is a save you can actually work with. What the museum
 * adds around it is the frame: the gilt edge and the plaque you were reading
 * on the wall a second ago, tinted for the kind of save it is, so the modal
 * lands as the painting you clicked rather than a generic dialog that happened
 * to appear.
 */

import React, { useCallback, useEffect } from 'react';
import { X, Heart, Bookmark, PenLine, MessageSquare, Quote } from 'lucide-react';
import TweetCard from './TweetCard';
import RedditCard from './RedditCard';
import RoamQuoteCard from './RoamQuoteCard';
import { isRoamSource } from '../utils';
import './PalaceSaveModal.css';

// Mat, rule and accent per style of save. The tweet tints descend from the
// warm PAPER palette the wall canvases are painted in and the quote tint from
// the cool VELLUM one (see museumCanvas.js), so the frame that opens is the
// colour of the frame you clicked. Within the tweets, the accent is what
// separates a like from a bookmark from something you wrote yourself.
const THEMES = {
  like: {
    label: 'Liked',
    Glyph: Heart,
    mat: '#f6efe6', rule: '#e0cfc4', accent: '#e0245e',
  },
  bookmark: {
    label: 'Bookmarked',
    Glyph: Bookmark,
    mat: '#eef3f7', rule: '#ccdde8', accent: '#1d9bf0',
  },
  user_tweet: {
    label: 'Yours',
    Glyph: PenLine,
    mat: '#f5f1e2', rule: '#ddd3b4', accent: '#9a7b2f',
  },
  reddit: {
    label: 'From Reddit',
    Glyph: MessageSquare,
    mat: '#f9efe8', rule: '#e8cdb9', accent: '#ff4500',
  },
  roam: {
    label: 'Quoted',
    Glyph: Quote,
    mat: '#f1edfb', rule: '#cdc4ef', accent: '#7c3aed',
  },
};

const themeFor = (save) => {
  if (isRoamSource(save.source)) return THEMES.roam;
  return THEMES[save.source] || THEMES.bookmark;
};

// Same dispatch the Saves list uses, and deliberately the same props: a card
// that behaves differently here than it does in the list is a card you have to
// learn twice.
const cardFor = (save, onUpdate) => {
  if (save.source === 'reddit') return <RedditCard post={save} />;
  if (isRoamSource(save.source)) return <RoamQuoteCard quote={save} />;
  return (
    <TweetCard
      tweet={save}
      onUpdateTweet={onUpdate}
      onEditStateChange={() => {}}
    />
  );
};

const PalaceSaveModal = ({ save, room, onClose, onUpdateSave }) => {
  const handleBackdrop = useCallback((event) => {
    if (event.target === event.currentTarget) onClose();
  }, [onClose]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    // Capture: the museum's own key handling sits on window and would
    // otherwise see the walk keys typed into an edit field below.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  if (!save) return null;

  const theme = themeFor(save);
  const { Glyph } = theme;

  return (
    <div className="palace-save-backdrop" onMouseDown={handleBackdrop}>
      <div
        className="palace-save-frame"
        role="dialog"
        aria-modal="true"
        aria-label={`${theme.label} save`}
        style={{
          '--save-mat': theme.mat,
          '--save-rule': theme.rule,
          '--save-accent': theme.accent,
        }}
      >
        <div className="palace-save-plaque">
          <Glyph className="palace-save-glyph" size={15} />
          <span className="palace-save-kind">{theme.label}</span>
          {room && <span className="palace-save-room">{room}</span>}
          <button
            type="button"
            className="palace-save-close"
            onClick={onClose}
            aria-label="Close and walk on"
          >
            <X size={18} />
          </button>
        </div>

        <div className="palace-save-mat">
          {cardFor(save, onUpdateSave)}
        </div>

        <div className="palace-save-hint">Esc, or click the wall, to walk on</div>
      </div>
    </div>
  );
};

export default PalaceSaveModal;
